/**
 * Advisory docintel collection handles.
 *
 * A V3 conversation owns exactly two docintel collections (ADR-014 — collection
 * membership is docintel's ONLY scoping model):
 *
 * - `{conversationId}_docs`     — documents the user uploaded to the engagement.
 * - `{conversationId}_internal` — the conversation's own node outputs, ingested
 *   as markdown so a later turn can retrieve what earlier nodes concluded. pgms
 *   is the only writer; this client derives it solely to report read scope.
 *
 * **Why this client derives rather than asks.** pgms' own
 * `graph_execution_v3/doc_intel/collections.py` states the reason: *"the frontend
 * needs the docs handle BEFORE the first turn exists"*. A document has to be in
 * the collection before the opening node reads it, and that node runs inside the
 * first turn — so the handle must be knowable while the conversation is still
 * nothing but an id. Deriving needs no round-trip, no provisioning step and no
 * ordering guarantee; docintel creates a collection implicitly on first ingest,
 * so an as-yet-unused handle is an empty read and never an error.
 *
 * The conversation id IS the advisory project id — the id in `/advisory/:projectId`
 * — because a project runs exactly one engagement. agent-svc defaults it that way
 * at both mint sites (`_prepare_analysis` and `_start_analysis`), which is what
 * lets this module take the route parameter and produce the handle a run will
 * actually read.
 *
 * A project prepared BEFORE that default existed carries a server-minted uuid4
 * instead, and its uploaded documents live under the handle derived from that.
 * Never derive for such a project — read `analysis.docsCollectionId` off the
 * document. `resolveDocsCollectionId` below is the one place that decides.
 *
 * **A handle is not a capability.** Deriving one grants nothing: gmid performs no
 * collection ownership check by design (it has no collection registry), and
 * authorisation stays with docintel's FORCE row-level security, whose membership
 * predicate keys on the validated uid gmid injects as `X-DocIntel-User` from the
 * Firebase token. So a caller who derives some other project's handle still reads
 * and writes only within their own corpus — the id is an address, and the token is
 * the authority. That is what makes it safe for this module to exist client-side.
 *
 * **Cross-language contract.** Identical to pgms' Python and to
 * `gaia3.0-showcase/src/lib/v3Collections.ts`. The namespace, the suffixes and
 * the normalisation are the shared contract, and all three pin the same
 * conformance vector (see this module's test). Changing any of them orphans every
 * collection already in Postgres — silently, because a handle that resolves to
 * nothing is an empty read.
 */

import { v5 as uuidv5 } from 'uuid';

/**
 * Duplicated verbatim from pgms `collections.py::V3_COLLECTION_NAMESPACE`.
 *
 * Immutable: it is baked into the `collection_id` of every row already in
 * `docintel.collections`, so changing it orphans them rather than failing.
 */
export const V3_COLLECTION_NAMESPACE = '0ba49081-a036-4026-b9f5-19c8c3cbe59b';

/** Suffix forming the uuid5 input for the user-documents collection. */
export const DOCS_SUFFIX = '_docs';

/** Suffix forming the uuid5 input for the conversation's own node outputs. */
export const INTERNAL_SUFFIX = '_internal';

/**
 * The exact whitespace set Python's `str.strip()` removes with no argument,
 * spelled out rather than using `String.prototype.trim()`.
 *
 * This is not pedantry. `trim()` removes every Unicode space plus the BOM, which
 * Python's default strip does not; Python's removes the C0 separators
 * `\x1c-\x1f`, which `trim()` does not. Either difference makes the two
 * implementations disagree for padded input, and a disagreement here is a handle
 * that resolves to an empty read rather than an error.
 */
const STRIPPED_WHITESPACE = [' ', '\t', '\n', '\r', '\f', '\v'];

function strip(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && STRIPPED_WHITESPACE.includes(value[start])) {
    start += 1;
  }
  while (end > start && STRIPPED_WHITESPACE.includes(value[end - 1])) {
    end -= 1;
  }
  return value.slice(start, end);
}

function handle(conversationId: string, suffix: string): string {
  const normalised = strip(conversationId ?? '');
  if (!normalised) {
    // Mirrors pgms, which raises rather than returning a handle for a blank id.
    // A uuid5 of just the suffix would be a real, stable UUID pointing at a
    // collection shared by every blank caller — worse than an error.
    throw new Error('conversationId must be non-empty to derive a docintel collection handle');
  }
  return uuidv5(`${normalised}${suffix}`, V3_COLLECTION_NAMESPACE);
}

/** Handle for the conversation's user-uploaded documents. The only one a client writes to. */
export function docsCollectionId(conversationId: string): string {
  return handle(conversationId, DOCS_SUFFIX);
}

/** Handle for the conversation's own node outputs. Reported, never written by a client. */
export function internalCollectionId(conversationId: string): string {
  return handle(conversationId, INTERNAL_SUFFIX);
}

/**
 * The docs handle to upload into for a project — the one place that decides
 * whether to derive or to read.
 *
 * `stored` is `analysis.docsCollectionId` off the project document when the
 * engagement has already been prepared. It WINS whenever present, because a
 * project prepared before the conversation id became the project id carries a
 * server-minted uuid4, and every document already uploaded for it sits under the
 * handle derived from that. Deriving for such a project would point the upload at
 * a different, empty collection — the user's files would still exist and simply
 * never be read.
 *
 * Falling back to the derivation is what makes an upload possible on a fresh
 * project, before anything has been prepared and before any turn exists.
 */
export function resolveDocsCollectionId(projectId: string, stored?: string | null): string {
  const trusted = strip(stored ?? '');
  return trusted || docsCollectionId(projectId);
}
